import React from "react"
import PropTypes from "prop-types"
import { Flex, Typography, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { Bar } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { ANNUAL_SUMMARY_COLORS, CHART_COLORS, DONUT_COLORS, barChartOptions } from "../../constants"
import { normalizeChartLabel, formatValue } from "../../utils"
import styles from "../../style.module.css"

const { Text } = Typography

export default function AnnualSummaryWidget({ data, description, title, value_format }) {
  const { t } = useTranslation()
  // chart_data for bar: { labels, series: [{ name, data }] }
  const labels = (data?.labels ?? []).map((l) => normalizeChartLabel(l))
  const series = Array.isArray(data?.series) ? data.series : []
  const seriesColors = [ANNUAL_SUMMARY_COLORS.income, ANNUAL_SUMMARY_COLORS.expenses, CHART_COLORS.green, CHART_COLORS.purple]

  const chartData = {
    labels,
    datasets: series.map((s, i) => ({
      label: s.name,
      data: s.data,
      backgroundColor: seriesColors[i] ?? DONUT_COLORS[i % DONUT_COLORS.length]
    }))
  }

  const chartOptions = {
    ...barChartOptions,
    plugins: {
      ...barChartOptions.plugins,
      tooltip: {
        ...barChartOptions.plugins.tooltip,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatValue(ctx.parsed.y, value_format)}`
        }
      }
    },
    scales: {
      ...barChartOptions.scales,
      y: {
        ...barChartOptions.scales.y,
        ticks: {
          ...barChartOptions.scales.y.ticks,
          callback: (value) => formatValue(value, value_format)
        }
      }
    }
  }

  return (
    <div className={styles.widgetContent}>
      <Flex justify="space-between" align="flex-start" className={styles.widgetHeader}>
        <div>
          <Text strong>{title ?? t("ai_dashboard.widget_annual_summary")}</Text>
          <div><Text type="secondary" className={styles.annualSubtitle}>{t("ai_dashboard.annual_subtitle")}</Text></div>
        </div>
        {description && (
          <Tooltip title={description} placement="topRight" styles={{ body: { maxWidth: 320 } }}>
            <InfoCircleOutlined className={styles.widgetInfoIcon} />
          </Tooltip>
        )}
      </Flex>
      <div className={styles.chartWrapper}>
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  )
}

AnnualSummaryWidget.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  description: PropTypes.string,
  title: PropTypes.string,
  value_format: PropTypes.string,
}
